import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import { api } from "../lib/api";
import { langsFor, LANGS } from "../lib/i18n";
import { RegionCard } from "../components/RegionCard";
import { Button } from "../components/ui/button";
import { CalendarClock, LogOut, RefreshCw, Wifi, WifiOff, Stethoscope, Globe2, UserCircle2, Sunrise, Siren, Utensils, Users } from "lucide-react";
import { toast } from "sonner";
import { SubscriptionCard, VideosCard, RemindersCard, DataCard, InstallCard } from "../components/ProfileExtras";
import { PatientTelesoinCard } from "../components/telesoin/PatientTelesoinCard";
import { PatientFacturesCard } from "../components/telesoin/Factures";
import { PatientRappelCard } from "../components/telesoin/RappelVocal";
import { briefEnabled, setBriefEnabled } from "../components/MorningBrief";

export default function ProfilePage() {
  const { user, logout, setUser } = useAuth();
  const { lang, setLang, online, pending, syncNow, patient, setPatient, profile } = useApp();
  const [appt, setAppt] = useState(null);
  const [brief, setBrief] = useState(briefEnabled());
  const toggleBrief = () => { setBriefEnabled(!brief); setBrief(!brief); };
  const [urgence, setUrgence] = useState(user?.numero_urgence || "");
  const [mealReminder, setMealReminder] = useState(patient?.rappel_repas !== false);
  useEffect(() => { setMealReminder(patient?.rappel_repas !== false); }, [patient?.rappel_repas]);
  const toggleMealReminder = async () => {
    const next = !mealReminder;
    setMealReminder(next);
    try { await api.put(`/patients/${patient.id}/meal-reminder`, { actif: next }); setPatient({ ...patient, rappel_repas: next }); toast.success(next ? "Rappel repas activé" : "Rappel repas désactivé"); }
    catch { setMealReminder(!next); toast.error("Enregistrement impossible"); }
  };
  const saveUrgence = async () => {
    try { const { data } = await api.patch("/auth/me", { numero_urgence: urgence.trim() || "112" }); setUser(data); toast.success("Numéro d'urgence enregistré"); }
    catch { toast.error("Enregistrement impossible"); }
  };
  const [contacts, setContacts] = useState(() => { const c = user?.contacts_urgence || []; return [c[0] || { nom: "", telephone: "" }, c[1] || { nom: "", telephone: "" }]; });
  const setContact = (i, k, v) => setContacts((cs) => cs.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const saveContacts = async () => {
    const list = contacts.map((c) => ({ nom: c.nom.trim(), telephone: c.telephone.trim() })).filter((c) => c.telephone);
    if (list.some((c) => c.telephone.replace(/[^\d+]/g, "").length < 6)) { toast.error("Numéro de téléphone invalide"); return; }
    try { const { data } = await api.patch("/auth/me", { contacts_urgence: list }); setUser(data); toast.success(list.length ? `${list.length} proche(s) enregistré(s)` : "Proches retirés"); }
    catch { toast.error("Enregistrement impossible"); }
  };
  const navigate = useNavigate();

  useEffect(() => {
    if (!patient) return;
    api.get(`/appointments?patient_id=${patient.id}`)
      .then(({ data }) => {
        const next = data.find((a) => a.statut === "planifie" && a.type === "presentiel");
        setAppt(next || null);
      });
  }, [patient]);

  const doLogout = async () => { await logout(); navigate("/auth/login", { replace: true }); };

  return (
    <div className="bc-in pt-2 pb-6 flex flex-col gap-4">
      <button data-testid="profile-boutique-btn" onClick={() => navigate("/boutique")} className="bg-[#0A1426] text-white rounded-3xl p-4 flex items-center gap-3 text-left">
        <span className="w-12 h-12 rounded-2xl bg-[#D4AF5A]/20 text-[#D4AF5A] grid place-items-center font-[Fraunces] text-xl font-bold">B</span>
        <span className="flex-1"><span className="block font-[Fraunces] text-base font-bold">Boutique Bombacare</span><span className="block text-[12px] text-white/70">Formations et programmes à l'unité — vidéo + PDF</span></span>
      </button>
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5 flex items-center gap-4">
        <div className="w-16 h-16 grid place-items-center rounded-2xl bg-[#F5F3EE] text-[#2BA3A0]">
          <UserCircle2 size={40} />
        </div>
        <div className="flex-1">
          <div className="font-[Fraunces] text-xl font-bold text-[#0A1426]">{user?.nom}</div>
          <div className="text-[12px] text-[#0A1426]/70 capitalize">{user?.role}</div>
          <div className="text-[12px] text-[#0A1426]/60 mt-0.5">{user?.email}</div>
        </div>
      </div>

      <SubscriptionCard />

      {patient && (
        <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
          <div className="flex items-center gap-2 mb-2 text-[#0A1426]/70">
            <Stethoscope size={16} />
            <span className="text-[11px] uppercase tracking-wider font-bold">Dossier</span>
          </div>
          <div className="font-[Fraunces] text-lg font-bold text-[#0A1426]">{patient.diagnostic}</div>
          <div className="text-[13px] text-[#0A1426]/70 mt-1">
            Côté atteint : <span className="font-bold capitalize">{patient.cote_atteint}</span>
          </div>
          {patient.date_avc && (
            <div className="text-[13px] text-[#0A1426]/70">
              Depuis le {new Date(patient.date_avc).toLocaleDateString("fr-FR")}
            </div>
          )}
        </div>
      )}

      {patient && <PatientRappelCard patientId={patient.id} />}
      {patient && <PatientTelesoinCard patientId={patient.id} />}
      {patient && <PatientFacturesCard patientId={patient.id} />}

      {appt && (
        <div data-testid="next-appt" className="bg-[#0A1426] rounded-3xl p-5 text-white">
          <div className="flex items-center gap-2 text-white/80">
            <CalendarClock size={16} />
            <span className="text-[11px] uppercase tracking-wider font-bold">Prochain rendez-vous</span>
          </div>
          <div className="font-[Fraunces] text-xl font-bold mt-1">Présentiel</div>
          <div className="text-[14px] text-white/85 mt-1">
            {new Date(appt.datetime).toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" })}
          </div>
        </div>
      )}

      {/* Language */}
      <RegionCard />
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
        <div className="flex items-center gap-2 mb-3 text-[#0A1426]/70">
          <Globe2 size={16} />
          <span className="text-[11px] uppercase tracking-wider font-bold">Langue</span>
        </div>
        <div className="flex gap-2">
          {(profile?.langues ? LANGS.filter((l) => profile.langues.includes(l.code) || l.code === "ln") : langsFor(user?.region)).map((l) => (
            <button
              key={l.code}
              data-testid={`lang-${l.code}`}
              onClick={() => setLang(l.code)}
              className={`flex-1 h-12 rounded-full font-bold ${
                lang === l.code ? "bg-[#2BA3A0] text-white" : "bg-[#F5F3EE] text-[#0A1426]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {/* Résumé vocal du matin */}
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5" data-testid="brief-card">
        <div className="flex items-center gap-2 mb-2 text-[#0A1426]/70">
          <Sunrise size={16} />
          <span className="text-[11px] uppercase tracking-wider font-bold">Résumé vocal matin & soir</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-[13px] text-[#0A1426]/80 leading-snug">Le matin, l'application lit le programme du jour et les rappels ; à partir de 18 h, elle félicite pour ce qui est fait et rappelle ce qui reste.</p>
          <button data-testid="brief-toggle" role="switch" aria-checked={brief} onClick={toggleBrief}
            className={`relative w-14 h-8 rounded-full shrink-0 transition-colors ${brief ? "bg-[#2BA3A0]" : "bg-[#E4E0D5]"}`}>
            <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${brief ? "translate-x-7" : "translate-x-1"}`} />
          </button>
        </div>
        <div data-testid="brief-state" className="text-[12px] font-bold mt-2" style={{ color: brief ? "#2BA3A0" : "#8A6D2B" }}>{brief ? "Activé" : "Désactivé"}</div>
      </div>

      {/* Rappel photo repas */}
      {patient && (
        <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5" data-testid="meal-reminder-card">
          <div className="flex items-center gap-2 mb-2 text-[#0A1426]/70">
            <Utensils size={16} />
            <span className="text-[11px] uppercase tracking-wider font-bold">Rappel photo repas</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-[13px] text-[#0A1426]/80 leading-snug">Si vous avez l'habitude de photographier un repas et que vous l'oubliez, l'app vous le rappelle (8h30, 13h30, 20h).</p>
            <button data-testid="meal-reminder-toggle" role="switch" aria-checked={mealReminder} onClick={toggleMealReminder}
              className={`relative w-14 h-8 rounded-full shrink-0 transition-colors ${mealReminder ? "bg-[#2BA3A0]" : "bg-[#E4E0D5]"}`}>
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${mealReminder ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </div>
          <div data-testid="meal-reminder-state" className="text-[12px] font-bold mt-2" style={{ color: mealReminder ? "#2BA3A0" : "#8A6D2B" }}>{mealReminder ? "Activé" : "Désactivé"}</div>
        </div>
      )}

      {/* Numéro d'urgence */}
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5" data-testid="emergency-card">
        <div className="flex items-center gap-2 mb-2 text-[#0A1426]/70">
          <Siren size={16} />
          <span className="text-[11px] uppercase tracking-wider font-bold">Numéro d'urgence</span>
        </div>
        <p className="text-[13px] text-[#0A1426]/80 leading-snug mb-2">Affiché sur la fiche « Alertes urgentes » de l'aidant. Par défaut : 112 / 113 (RDC).</p>
        <div className="flex gap-2">
          <input data-testid="emergency-input" type="tel" value={urgence} onChange={(e) => setUrgence(e.target.value)} placeholder="Ex. +243 81 000 00 00 ou 112"
            className="flex-1 h-12 px-4 rounded-full bg-[#F5F3EE] text-[#0A1426] font-bold focus:outline-none focus:ring-2 focus:ring-[#2BA3A0]" />
          <Button data-testid="emergency-save" onClick={saveUrgence} className="h-12 px-5 rounded-full bg-[#0A1426] text-white font-bold">OK</Button>
        </div>
      </div>

      {/* Proches à prévenir (aidant) */}
      {user?.role === "aidant" && (
        <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5" data-testid="family-contacts-card">
          <div className="flex items-center gap-2 mb-2 text-[#0A1426]/70">
            <Users size={16} />
            <span className="text-[11px] uppercase tracking-wider font-bold">Proches à prévenir en urgence</span>
          </div>
          <p className="text-[13px] text-[#0A1426]/80 leading-snug mb-3">Jusqu'à 2 proches recevront automatiquement un SMS avec les signes et votre position quand vous appuyez sur « Je suis inquiet ».</p>
          <div className="flex flex-col gap-2">
            {contacts.map((c, i) => (
              <div key={i} className="grid grid-cols-5 gap-2">
                <input data-testid={`family-contact-name-${i}`} value={c.nom} onChange={(e) => setContact(i, "nom", e.target.value)} placeholder={i === 0 ? "Ex. Tante Marie" : "Ex. Oncle Patrick"}
                  className="col-span-2 h-12 px-4 rounded-full bg-[#F5F3EE] text-[#0A1426] font-bold text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2BA3A0]" />
                <input data-testid={`family-contact-phone-${i}`} type="tel" value={c.telephone} onChange={(e) => setContact(i, "telephone", e.target.value)} placeholder="+243 81 000 00 00"
                  className="col-span-3 h-12 px-4 rounded-full bg-[#F5F3EE] text-[#0A1426] font-bold text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2BA3A0]" />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-3">
            <span data-testid="family-contacts-count" className="text-[12px] text-[#0A1426]/60">{(user?.contacts_urgence || []).length} proche(s) enregistré(s)</span>
            <Button data-testid="family-contacts-save" onClick={saveContacts} className="h-12 px-5 rounded-full bg-[#0A1426] text-white font-bold">Enregistrer</Button>
          </div>
        </div>
      )}

      {/* Connection */}
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
        <div className="flex items-center gap-2 mb-2 text-[#0A1426]/70">
          {online ? <Wifi size={16} /> : <WifiOff size={16} />}
          <span className="text-[11px] uppercase tracking-wider font-bold">Connexion</span>
        </div>
        <div className="font-[Fraunces] text-base font-bold text-[#0A1426]">
          {online ? (pending > 0 ? `${pending} élément(s) à synchroniser` : "Tout est à jour") : `Hors ligne · ${pending} en attente`}
        </div>
        <Button
          data-testid="sync-now-btn"
          onClick={syncNow}
          disabled={!online || pending === 0}
          className="w-full h-12 mt-3 rounded-full bg-[#2BA3A0] hover:bg-[#0A1426] text-white font-bold disabled:opacity-50"
        >
          <RefreshCw size={16} className="mr-2" /> Synchroniser maintenant
        </Button>
      </div>

      <InstallCard />
      <VideosCard />
      <RemindersCard />
      <DataCard />

      <Button
        data-testid="logout-btn"
        onClick={doLogout}
        variant="outline"
        className="h-12 rounded-full border-[#D9534F] text-[#D9534F] hover:bg-[#D9534F] hover:text-white font-bold"
      >
        <LogOut size={16} className="mr-2" /> Se déconnecter
      </Button>
    </div>
  );
}
